import {
  ROUTE_PAYMENT,
  ROUTE_SIGN_IN,
  ROUTE_SIGN_UP,
} from '@/constants/routes';
import { getRoute } from '@/utils';
import axios, { isAxiosError } from 'axios';
import authService from '../services/auth.service';
import envService from '../services/env.service';
import sentryService from '../services/sentry.service';
import { attachAuthToken, shouldRedirectOn401 } from './auth-interceptor';

/**
 * Singleton to make requests to the server
 */
const apiClient = axios.create({
  baseURL: envService.getEnv().VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

apiClient.interceptors.request.use(attachAuthToken);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error === undefined) {
      return Promise.reject('Unknown error');
    }
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }

    // Capture API errors in Sentry with context.
    // Exclude expected responses handled gracefully by the UI:
    // - 405 "Risk not processed" = user risk is still being calculated, redirected to loading screen
    const isExpectedError =
      error.response?.status === 405 && error.response?.data?.title === 'Risk not processed';

    if (error.response && !isExpectedError) {
      sentryService.captureException(error, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        statusText: error.response.statusText,
        responseData: error.response.data,
      });
    }

    // window.location.href triggers a full page reload that terminates any
    // in-flight JS — so we await authService.logout() (which invalidates the
    // WorkOS server-side cookie) BEFORE reloading. Otherwise the next page
    // boot re-hydrates the WorkOS session from a still-live cookie. After the
    // location change is queued we still propagate the rejection so callers
    // (React Query, components) see the original error.
    if (error?.response?.status === 401 && error?.response?.data?.message !== 'Session unauthorized') {
      if (shouldRedirectOn401()) {
        return authService.logout().finally(() => {
          window.location.href = getRoute(ROUTE_SIGN_IN);
        }).then(() => Promise.reject(error), () => Promise.reject(error));
      }
      return Promise.reject(error);
    } else if (error?.response?.status === 401 && error?.response?.data?.message === 'Session unauthorized') {
      authService.logoutVisitor();
      return Promise.reject(error);
    } else if (error?.response?.status === 402) {
      return authService.logout().finally(() => {
        window.location.href = getRoute(ROUTE_PAYMENT);
      }).then(() => Promise.reject(error), () => Promise.reject(error));
    } else if (error?.response?.status === 403 && error.response.data.title === 'Invite required') {
      return authService.logout().finally(() => {
        window.location.href = getRoute(ROUTE_SIGN_UP);
      }).then(() => Promise.reject(error), () => Promise.reject(error));
    } else if (error?.response?.status === 405 && error?.response?.data.title === 'Risk not processed') {
      // Legacy behavior: member-backend returns 405 when risk hasn't been processed yet.
      // With catch-backend handling risk via Temporal, these 405s are expected on legacy
      // endpoints (screeningFollowUp, action-plan, etc.) and should NOT trigger a redirect —
      // the loading screen polls catch-backend's status endpoint which is unrelated.
      // Let the error propagate so React Query handles it with retry/error states.
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
